/**
 * Call Category Types & Constants
 *
 * 10 call categories from categorize-step + per-category toggle defaults.
 * Used by system-builder, analysis-guidelines, and output-format.
 */

import type { AnalyzedCallCategory } from '@aica/api-types';
import { ANALYZED_CALL_CATEGORIES, PURCHASE_INTENT_CATEGORIES } from '@aica/api-types';

/**
 * Deliberate subset of `callCategorySchema` (`@aica/api-types`, the
 * canonical 12-value list of every record category): `missed` and `short`
 * are absent on purpose, not by oversight — a missed or too-short call is
 * never analyzed, so there is nothing to bind an analysis profile to. These
 * 10 values ARE `ProfileBinding`/`ScriptBinding` in full: calls and chats
 * are categorized and bound the same way, so there is no separate `chat`
 * value.
 *
 * There is no `satisfies` clause here any more, and none is needed: this is
 * not a hand-rolled list checked against the canonical one, it IS the
 * canonical one. `callCategorySchema` is built FROM it
 * (`z.enum(['missed', 'short', ...ANALYZED_CALL_CATEGORIES])`,
 * api-types/src/schemas/records.ts), so the subset relation holds by
 * construction and a category renamed upstream cannot go stale here — the
 * alias moves with it.
 *
 * That guarantee is a property of the ALIAS. If this constant is ever
 * re-hand-rolled into a literal list (the shape it had before), restore an
 * explicit `satisfies readonly CanonicalCallCategory[]` in the same edit —
 * without it a value that fell out of the schema upstream compiles silently.
 */
export const CALL_CATEGORIES = ANALYZED_CALL_CATEGORIES;

export type CallCategory = AnalyzedCallCategory;

export type Channel = 'call' | 'chat';

/**
 * Categories where saleProbability makes sense. Built from the shared
 * purchase-intent list (@aica/api-types) — see that constant's own doc
 * comment for why it is shared across three unrelated questions today and
 * when to stop sharing it (split, don't edit the shared list for this
 * consumer's needs alone).
 */
export const SALES_CATEGORIES: ReadonlySet<CallCategory> = new Set(
  PURCHASE_INTENT_CATEGORIES
);

/**
 * Map legacy BaseCategory (prompt profile category) to CallCategory.
 * Used as fallback when callCategory is not set.
 */
export function baseToCallCategory(
  baseCategory: string,
  channel: Channel = 'call'
): CallCategory {
  switch (baseCategory) {
    case 'sales-call':
      return 'purchase';
    case 'sales-chat':
      return 'purchase';
    case 'support-call':
      return 'support';
    case 'complaint':
      return 'complaint';
    default:
      return channel === 'chat' ? 'other' : 'purchase';
  }
}

/**
 * Check if a category is sales-related (saleProbability should be enabled)
 */
export function isSalesCategory(category: CallCategory): boolean {
  return SALES_CATEGORIES.has(category);
}
